import Markdown, { MarkdownToJSX } from "markdown-to-jsx";
import React from "react";
import { processMessageWithCitations } from "@shared/components/Assistant/MessageProcessor";
import { InlineBadge } from "@pages/Assistant/utils/messageParts";
import { convertS3UrlToHttps } from "@pages/Assistant/utils/utils";

export const useRenderHelper = (
  overrides: MarkdownToJSX.Overrides | undefined,
  inlineBadgesRef: React.RefObject<InlineBadge[]>,
  setShowDocxModal: (show: boolean) => void,
  setDocxContent: (content: string) => void,
  setExcelData: (data: any[][]) => void,
  setShowExcelModal: (show: boolean) => void,
  addMessageToChat: (
    message: string | React.ReactNode | React.ReactNode[],
    sender: "user" | "bot",
    isChunk?: boolean,
    citations?: any[],
    generated_images_s3_keys?: any
  ) => void,
  setChatLog: (
    value: React.SetStateAction<
      {
        message: string | React.ReactNode | React.ReactNode[];
        sender: "user" | "bot";
        citations?: any[];
        images?: any;
        generated_images_s3_keys?: any;
      }[]
    >
  ) => void,
  imageRef: React.RefObject<any[]>,
  setModalImageSrc: React.Dispatch<React.SetStateAction<string | null>>,
  setModalImageAlt: React.Dispatch<React.SetStateAction<string>>,
  setShowImageModal: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const IMG_TOKEN_REGEX = /\{\[IMG:(\d+)\]\}/g;

  const renderProcessedChunk = (processed: React.ReactNode) => {
    if (React.isValidElement(processed)) return processed;
    if (typeof processed === "string") {
      return (
        <Markdown options={{ overrides: overrides }}>{processed}</Markdown>
      );
    }
    return <div className="text-red-500">⚠️ Invalid message format</div>;
  };

  const buildInlineNodes = (text: string): React.ReactNode[] => {
    const nodes: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = IMG_TOKEN_REGEX.exec(text)) !== null) {
      const before = text.slice(lastIndex, match.index);
      if (before) {
        const processedBefore = processMessageWithCitations({
          message: before,
          citations: inlineBadgesRef.current,
          setShowDocxModal,
          setDocxContent,
          setShowExcelModal,
          setExcelData,
          showDocxModal: false,
          docxContent: "",
          showExcelModal: false,
          excelData: [],
        });
        nodes.push(renderProcessedChunk(processedBefore));
      }

      const idx = parseInt(match[1], 10);
      const imgObj = imageRef.current[idx];
      const src = convertS3UrlToHttps(imgObj);
      const alt = imgObj?.filename || `image-${idx}`;

      nodes.push(
        <img
          key={`inline-img-${idx}-${match.index}`}
          src={src || ""}
          alt={alt}
          className="inline-block align-middle w-[100%] rounded cursor-pointer border border-gray-200 mb-6 hover:border-gray-300 transition-colors shadow"
          onClick={() => {
            setModalImageSrc(src || null);
            setModalImageAlt(alt);
            setShowImageModal(true);
          }}
        />
      );

      lastIndex = match.index + match[0].length;
    }

    const after = text.slice(lastIndex);
    if (after) {
      const processedAfter = processMessageWithCitations({
        message: after,
        citations: inlineBadgesRef.current,
        setShowDocxModal,
        setDocxContent,
        setShowExcelModal,
        setExcelData,
        showDocxModal: false,
        docxContent: "",
        showExcelModal: false,
        excelData: [],
      });
      nodes.push(renderProcessedChunk(processedAfter));
    }

    return nodes;
  };

  const renderBotMessage = (text: string) => {
    const nodes = buildInlineNodes(text);

    addMessageToChat(nodes, "bot", true, undefined, imageRef.current);

    setChatLog((prev) => {
      const updated = [...prev];
      const lastBotIndex = updated.map((m) => m.sender).lastIndexOf("bot");
      if (lastBotIndex !== -1) {
        const prevImages = updated[lastBotIndex].generated_images_s3_keys ?? [];
        updated[lastBotIndex] = {
          ...updated[lastBotIndex],
          message: nodes,
          generated_images_s3_keys:
            (imageRef.current?.length ? imageRef.current : prevImages) ?? [],
        } as any;
      }
      return updated;
    });
  };

  return {
    renderBotMessage,
    IMG_TOKEN_REGEX,
  };
};
